import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, RefreshCw } from "lucide-react";

import { VenueDrawer } from "@/components/maps/VenueDrawer";
import { VenueMap } from "@/components/maps/VenueMap";
import { useVenueGeo } from "@/components/maps/useVenueGeo";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import type { VenueMapView } from "@/types/venues";

const VIEW_OPTIONS: { label: string; value: VenueMapView }[] = [
  { label: "World Map", value: "world" },
  { label: "Heatmap", value: "heat" },
  { label: "Clusters Only", value: "clusters" },
  { label: "All Pins", value: "pins" },
];

const SOURCE_LABELS = {
  crm: "CRM contacts",
  routing: "Routing pipeline",
  stub: "Stubbed demo data",
} as const;

export default function VenueMapPage() {
  const { featureCollection, stats, venues, loading, error, reload, sourceId, dataSource } = useVenueGeo();
  const [viewMode, setViewMode] = useState<VenueMapView>("world");
  const [selectedVenueId, setSelectedVenueId] = useState<string | null>(null);

  const selectedVenue = useMemo(
    () => venues.find((venue) => String(venue.id) === selectedVenueId) ?? null,
    [selectedVenueId, venues],
  );

  const handleSelectVenue = (venueId: string) => {
    setSelectedVenueId(venueId);
  };

  const handleDismissDrawer = () => {
    setSelectedVenueId(null);
  };

  const handleViewModeChange = (value: string) => {
    if (!value) return;
    setViewMode(value as VenueMapView);
  };

  return (
    <div className="aa-page-shell space-y-4">
      <header className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="aa-page-title">
            Venue <span className="aa-gradient-text">Map</span>
          </h1>
          <p className="text-sm text-muted-foreground">
            Plot CRM venues worldwide, cluster targets, and hand off qualified rooms to routing.
          </p>
        </div>
        <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:gap-3">
          <ToggleGroup
            type="single"
            value={viewMode}
            onValueChange={handleViewModeChange}
            className="flex flex-wrap gap-2"
          >
            {VIEW_OPTIONS.map((option) => (
              <ToggleGroupItem key={option.value} value={option.value}>
                {option.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              reload().catch(() => {});
            }}
          >
            <RefreshCw className={loading ? "mr-2 h-4 w-4 animate-spin" : "mr-2 h-4 w-4"} />
            Refresh
          </Button>
        </div>
      </header>

      <section className="grid gap-4 text-sm text-foreground/70 sm:grid-cols-2 lg:grid-cols-4">
        <div className="aa-glass-card p-4">
          <p className="text-xs uppercase tracking-wide text-primary">Total venues</p>
          <p className="mt-1 text-2xl font-semibold text-foreground">
            {loading ? "—" : stats.total.toLocaleString()}
          </p>
          <p className="text-xs text-muted-foreground">
            Source: {SOURCE_LABELS[dataSource] ?? "Unknown"}.
          </p>
        </div>
        <div className="aa-glass-card p-4">
          <p className="text-xs uppercase tracking-wide text-emerald-200">US Venues</p>
          <p className="mt-1 text-2xl font-semibold text-foreground">
            {loading ? "—" : stats.domestic.toLocaleString()}
          </p>
          {!loading ? (
            <Badge
              variant="secondary"
              className={
                dataSource === "crm" && stats.missingGeocodes === 0
                  ? "mt-1 bg-emerald-500/20 text-emerald-200"
                  : "mt-1 bg-amber-500/20 text-amber-200"
              }
            >
              {dataSource === "crm" && stats.missingGeocodes === 0 ? "Route ready" : "Needs geocoding"}
            </Badge>
          ) : null}
        </div>
        <div className="aa-glass-card p-4">
          <p className="text-xs uppercase tracking-wide text-sky-200">International</p>
          <p className="mt-1 text-2xl font-semibold text-foreground">
            {loading ? "—" : stats.international.toLocaleString()}
          </p>
          <p className="text-xs text-muted-foreground">Tier + tags available inside the drawer.</p>
        </div>
        <div className="aa-glass-card p-4">
          <p className="text-xs uppercase tracking-wide text-rose-200">Needs precise geocode</p>
          <p className="mt-1 text-2xl font-semibold text-foreground">
            {loading ? "—" : stats.missingGeocodes.toLocaleString()}
          </p>
          <p className="text-xs text-muted-foreground">
            Includes approximate state-level pins.
          </p>
        </div>
      </section>

      {error ? (
        <div className="rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-200">
          Failed to load venues: {error}
        </div>
      ) : null}

      <div className="relative -mx-4 sm:-mx-6 lg:-mx-10">
        <div className="relative w-full">
          <div className="relative h-[calc(100vh-240px)] min-h-[520px] w-full">
            <VenueMap
              collection={featureCollection}
              sourceId={sourceId}
              viewMode={viewMode}
              onSelectVenue={handleSelectVenue}
              selectedVenueId={selectedVenueId}
            />
            {loading ? (
              <div className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-background/40 backdrop-blur-sm">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
                <span className="ml-3 text-sm text-foreground/80">Loading venues…</span>
              </div>
            ) : null}
            {!loading && stats.total === 0 ? (
              <div className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-background/60 backdrop-blur-sm">
                <div className="max-w-sm rounded-xl border border-border/70 bg-background/80 p-5 text-center shadow-lg">
                  <p className="text-sm font-semibold text-foreground">No venues mapped yet</p>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Add venues or resolve geocodes to populate the map.
                  </p>
                  <Button asChild size="sm" className="mt-4">
                    <Link to="/venue-queue">Open Venue Queue</Link>
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
        <span className="uppercase tracking-wide text-foreground/70">Legend</span>
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
          Precise US venue
        </div>
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full bg-sky-400" />
          Precise international
        </div>
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full bg-rose-400" />
          Approximate location
        </div>
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/60" />
          Size scales by capacity
        </div>
      </div>

      <VenueDrawer venue={selectedVenue} open={Boolean(selectedVenue)} onClose={handleDismissDrawer} />
    </div>
  );
}
